import {
  ButtonGroup,
  ModalProps,
  PageBody,
  PageSection,
  Spacer,
  Stack,
  Text,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';
import React, { useEffect, useMemo, useRef } from 'react';

import homepageEmptyStateBg from '../../assets/homepage-empty-state.svg?url';
import {
  CreateProjectButton,
  CreateProjectFlow,
  PageHeader,
  ProductCardsModal,
} from '../../components';
import { Skeleton } from '../../components/skeleton';
import { OrganizationRoleNames, useAuthRole } from '../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatchesAllOf,
} from '../../xstate/app.xstate';
import { IPage } from '../types';
import { ProjectsTable } from './projects-table';

import './homepage.scss';

export const Homepage: React.FC<IPage> = () => {
  const isReadyToLoad = useManagerStateMatchesAllOf([
    'orgRelated.ready.projectsNew.idle',
    'orgRelated.ready.currentOrg.selected',
  ]);
  const loading = !useManagerStateMatchesAllOf([
    'orgRelated.ready.projectsNew.loaded',
  ]);
  const modalRef = useRef<ModalProps>(null);

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const { projectsNew, selectedProjectId, selectedProject } =
    useManagerCtxSelector((ctx) => ({
      projectsNew: ctx.projectsNew,
      selectedProjectId: ctx.selectedProjectModal,
      selectedProject: ctx.projectsNew?.find(
        (p) => p?.id === ctx.selectedProjectModal
      ),
    }));

  useEffect(() => {
    if (isReadyToLoad) {
      sendManagerEvent('manager.loadNewProjects');
    }
  }, [isReadyToLoad]);

  const createProjectButton = useMemo(
    () => (
      <CreateProjectButton
        disabled={loading}
        onClick={() => {
          if (modalRef.current) {
            modalRef.current.open = true;
          }
        }}
      />
    ),
    [modalRef, loading]
  );

  const isEmptyState = useMemo(
    () => projectsNew?.length === 0 || loading,
    [projectsNew, loading]
  );

  const renderEmptyState = useMemo(() => {
    return (
      <div
        style={{
          backgroundImage: `url(${homepageEmptyStateBg})`,
          backgroundRepeat: 'no-repeat',
          // backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '250px',
          flexGrow: 1,
          marginLeft: '-3rem',
          marginRight: '-3rem',
        }}
      >
        <Text
          size="medium"
          fontWeight="medium"
          style={
            {
              '--ps-theme-text-color': '#BCB2F2',
            } as React.CSSProperties
          }
        >
          <TranslationComponent i18nKey="manager/common:homepage.projects.nothingToShowHere">
            Nothing to show here...{' '}
          </TranslationComponent>
        </Text>
        {isAdminRole && (
          <>
            <Text size="2xsmall" className="u-margin-bottom-large">
              <TranslationComponent i18nKey="manager/common:homepage.projects.toGetStartedCreateProject">
                To get started, create your first project.
              </TranslationComponent>
            </Text>

            {createProjectButton}
          </>
        )}
      </div>
    );
  }, [isAdminRole, createProjectButton]);

  const renderProjectsTable = useMemo(() => {
    return (
      <>
        {!loading && isEmptyState ? (
          <div {...{ 'test-id': 'homepage-projects-empty-state' }}>
            {renderEmptyState}
          </div>
        ) : (
          <>
            <section>
              <Stack alignItems="center" justifyContent="space-between">
                <TextTitle size="xsmall">
                  <TranslationComponent i18nKey="manager/common:homepage.projects.yourProjects">
                    Your Projects
                  </TranslationComponent>
                </TextTitle>
                {isAdminRole && (
                  <ButtonGroup>{createProjectButton}</ButtonGroup>
                )}
              </Stack>
            </section>
            <Spacer size="large" />
            {loading ? (
              <>
                <div {...{ 'test-id': 'homepage-projects-loading' }}>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              </>
            ) : (
              <div {...{ 'test-id': 'homepage-projects-table' }}>
                <ProjectsTable projects={projectsNew} />
              </div>
            )}
          </>
        )}
      </>
    );
  }, [
    loading,
    isEmptyState,
    renderEmptyState,
    isAdminRole,
    createProjectButton,
    projectsNew,
  ]);

  return (
    <PageBody width="large" background="none">
      <PageHeader />
      <CreateProjectFlow modalRef={modalRef} />
      <PageSection size="small">
        <ProductCardsModal
          project={selectedProject}
          open={!!selectedProjectId}
          onClose={() => {
            sendManagerEvent({
              type: 'manager.deselectProjectModal',
            });
          }}
        />
        {renderProjectsTable}
      </PageSection>
    </PageBody>
  );
};
